import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Infinity as InfinityIcon, Car, CalendarCheck, Users } from "lucide-react";
import { api } from "@/utils/api-client";
import { PageHero } from "../_components/page-hero";

/* ─────────────────────────────  types + helpers  ───────────────────────────── */

type ApiPlan = {
  id?: string;
  _id?: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  billingCycle: "monthly" | "yearly";
  features: string[];
  maxVehicles: number;
  maxBookings: number | null;
  maxCustomers: number | null;
  allowedModules: string[];
  isPublic: boolean;
  status: string;
};

const UNLIMITED_SENTINEL = 999999;
const OVERAGE_PER_VEHICLE = 20; // ₹ per extra vehicle beyond a paid plan's cap

// Human labels for the 8 toggleable modules (matches the backend ALL_MODULES).
const MODULE_LABELS: Record<string, { label: string; desc: string }> = {
  vehicles: { label: "Vehicles", desc: "Fleet records, vehicle status and per-vehicle pricing configuration." },
  bookings: { label: "Bookings", desc: "Creation, availability checks, pickup, active trips, returns, settlement and invoices." },
  customers: { label: "Customers", desc: "Customer records, KYC status, rental history and risk status." },
  payments: { label: "Payments", desc: "Payment records, collection tracking and refundable deposit settlement." },
  maintenance: { label: "Maintenance", desc: "Track vehicle servicing and maintenance-related availability." },
  documents: { label: "Documents", desc: "Store vehicle and customer documents and monitor expiry dates." },
  qr: { label: "QR access", desc: "Generate, download, print and share vehicle QR codes." },
  emails: { label: "Business email", desc: "Send booking and reminder emails from your own SMTP address." },
};
const ALL_MODULE_KEYS = Object.keys(MODULE_LABELS);

function inr(n: number): string {
  return "₹" + n.toLocaleString("en-IN");
}

/* ─────────────────────────────  static fallback  ───────────────────────────── */
// Mirrors the seeded DB plans so the page still renders if the API is unreachable.
const FALLBACK_PLANS: ApiPlan[] = [
  {
    name: "Free", slug: "free", description: "Explore RentOS and manage your first few rentals with the essential tools.",
    price: 0, billingCycle: "monthly",
    maxVehicles: 3, maxBookings: 20, maxCustomers: null,
    allowedModules: ["vehicles", "bookings", "documents"],
    features: ["Dashboard overview", "Booking management and rental schedules", "Rental agreements in the booking workflow"],
    isPublic: true, status: "active",
  },
  {
    name: "Starter", slug: "starter-monthly",
    description: `The complete RentOS platform — up to 25 vehicles, then ₹${OVERAGE_PER_VEHICLE} per extra vehicle. Unlimited bookings and customers.`,
    price: 1499, billingCycle: "monthly",
    maxVehicles: 25, maxBookings: null, maxCustomers: null,
    allowedModules: ALL_MODULE_KEYS,
    features: [
      `Up to 25 vehicles, then ₹${OVERAGE_PER_VEHICLE} per extra vehicle/month`,
      "Unlimited bookings and customers", "All available modules",
      "Complete booking-to-settlement workflow",
      "Customer KYC, signed agreements and document expiry monitoring",
      "Business email, notifications and activity history",
      "Responsive web app and native Android/iOS app",
      "CSV import/export and 24/7 support",
    ],
    isPublic: true, status: "active",
  },
  {
    name: "Growth", slug: "growth-annual",
    description: `Every RentOS module, billed annually for a lower monthly cost — up to 50 vehicles, then ₹${OVERAGE_PER_VEHICLE} per extra vehicle. Unlimited bookings and customers.`,
    price: 11988, billingCycle: "yearly",
    maxVehicles: 50, maxBookings: null, maxCustomers: null,
    allowedModules: ALL_MODULE_KEYS,
    features: [
      "Everything in Starter",
      `Up to 50 vehicles, then ₹${OVERAGE_PER_VEHICLE} per extra vehicle`,
      "Unlimited bookings and customers", "All available modules and features",
      "Customer KYC, signed agreements and document expiry monitoring",
      "Business email, notifications and activity history",
      "Responsive web app and native Android/iOS app", "24/7 support",
    ],
    isPublic: true, status: "active",
  },
];

/* ─────────────────────────────  page  ───────────────────────────── */

export default function PricingPage() {
  const [plans, setPlans] = useState<ApiPlan[]>(FALLBACK_PLANS);

  useEffect(() => {
    api.silent
      .get<ApiPlan[]>("/api/public/plans")
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setPlans(data);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const free = plans.find((p) => p.slug === "free" || p.price === 0);
  const starter = plans.find((p) => p.billingCycle === "monthly" && p.price > 0);
  const growth = plans.find((p) => p.billingCycle === "yearly" && p.price > 0);

  return (
    <>
      <PageHero
        eyebrow="Plans & pricing"
        title={<>Three plans. <span className="gradient-text">Start free.</span></>}
        sub="Start free, pay monthly, or save with annual billing. Paid plans unlock every RentOS module with unlimited bookings and customers."
      />

      <section className="pb-16 sm:pb-24">
        {/* items-stretch + h-full cards = equal heights across all three */}
        <div className="mx-auto grid max-w-6xl items-stretch gap-6 px-4 sm:px-6 lg:grid-cols-3">
          {free && <PlanCard plan={free} variant="free" />}
          {starter && <PlanCard plan={starter} variant="starter" />}
          {growth && <PlanCard plan={growth} variant="growth" starter={starter} />}
        </div>
      </section>

      {starter && growth && <BillingComparison starter={starter} growth={growth} />}

      {/* All modules reference */}
      <section className="border-t border-border py-20 sm:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="text-center">
            <div className="text-xs font-medium uppercase tracking-[0.18em] text-primary">What paid plans unlock</div>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Every available module.</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              Settings, notifications and reports are always available on every plan. These modules unlock with a paid plan.
            </p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ALL_MODULE_KEYS.map((k) => (
              <div key={k} className="rounded-2xl border border-border bg-surface/40 p-5">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  <h3 className="font-display text-base font-semibold">{MODULE_LABELS[k].label}</h3>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{MODULE_LABELS[k].desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-glow"
            >
              Talk to us about a plan
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

/* ─────────────────────────────  unified plan card  ─────────────────────────────
   One component for all three plans → identical structure + equal height.
   Layout: badge · name/tagline · price · usage limits · feature list · CTA (pinned). */

type Variant = "free" | "starter" | "growth";

function PlanCard({ plan, variant, starter }: { plan: ApiPlan; variant: Variant; starter?: ApiPlan }) {
  const highlight = variant !== "free";
  const isGrowth = variant === "growth";

  // Pricing display
  const monthlyEquivalent = isGrowth ? Math.round(plan.price / 12) : plan.price;
  const twelveMonthAtMonthly = starter ? starter.price * 12 : plan.price;
  const savings = isGrowth ? twelveMonthAtMonthly - plan.price : 0;
  const discountPct = twelveMonthAtMonthly > 0 ? (savings / twelveMonthAtMonthly) * 100 : 0;

  const badge = variant === "free" ? "Free forever" : variant === "starter" ? "Monthly" : "Annual · best value";
  const vehiclesLabel = plan.maxVehicles === UNLIMITED_SENTINEL ? "Unlimited" : `${plan.maxVehicles} vehicles`;
  const planParam = `/contact?plan=${encodeURIComponent(plan.slug)}`;
  const cta = variant === "free" ? "Get started free" : `Choose ${plan.name}`;

  return (
    <div
      className={`relative flex h-full flex-col rounded-2xl border p-6 sm:p-7 ${
        highlight ? "border-primary/50 bg-surface shadow-glow" : "border-border bg-surface/40"
      }`}
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
          {badge}
        </div>
      )}

      {/* Name + tagline */}
      <h3 className="font-display text-xl font-semibold">{plan.name}</h3>
      <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
        {variant === "free" ? "Free forever" : variant === "starter" ? "Monthly subscription" : "Billed annually"}
      </p>

      {/* Price block — fixed footprint so all cards align */}
      <div className="mt-5 min-h-[76px]">
        <div className="flex items-baseline gap-1">
          <span className="font-display text-4xl font-semibold tracking-tight">{inr(monthlyEquivalent)}</span>
          <span className="text-sm text-muted-foreground">
            {variant === "free" ? "/ forever" : isGrowth ? "/ mo equivalent" : "/ month"}
          </span>
        </div>
        {isGrowth ? (
          <p className="mt-1 text-xs text-muted-foreground">
            {inr(plan.price)} billed yearly
            {savings > 0 && <span className="ml-1 font-medium text-primary">· save {inr(savings)} ({discountPct.toFixed(0)}%)</span>}
          </p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">
            {variant === "free" ? "No card required" : "Cancel per your billing terms"}
          </p>
        )}
      </div>

      {/* Usage limits — compact row */}
      <div className="mt-5 grid grid-cols-3 gap-2">
        <UsageTile icon={Car} value={plan.maxVehicles === UNLIMITED_SENTINEL ? "∞" : String(plan.maxVehicles)} label="Vehicles" />
        <UsageTile icon={CalendarCheck} value={plan.maxBookings == null ? "∞" : String(plan.maxBookings)} label={variant === "free" ? "Bookings" : "Bookings"} />
        <UsageTile icon={Users} value={plan.maxCustomers == null ? "∞" : String(plan.maxCustomers)} label="Customers" />
      </div>
      {variant !== "free" && (
        <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
          {vehiclesLabel} included, then <span className="font-medium text-foreground">₹{OVERAGE_PER_VEHICLE}</span> per extra vehicle.
        </p>
      )}

      {/* Feature list */}
      <ul className="mt-6 mb-8 space-y-2.5 text-sm">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <span className="leading-snug">{f}</span>
          </li>
        ))}
        {variant === "free" && (
          <li className="flex items-start gap-2.5 text-muted-foreground">
            <InfinityIcon className="mt-0.5 h-4 w-4 flex-shrink-0 opacity-40" />
            <span className="leading-snug">Customers, payments, maintenance, QR &amp; email unlock on a paid plan.</span>
          </li>
        )}
      </ul>

      {/* CTA pinned to the bottom so every card is the same height */}
      <Link
        to={planParam}
        className={`mt-auto block rounded-xl px-4 py-2.5 text-center text-sm font-medium ${
          highlight
            ? "bg-gradient-primary text-primary-foreground shadow-glow"
            : "border border-border bg-background/40 hover:bg-surface"
        }`}
      >
        {cta}
      </Link>
    </div>
  );
}

function UsageTile({ icon: Icon, value, label }: { icon: typeof Car; value: string; label: string }) {
  return (
    <div className="rounded-xl border border-border bg-background/50 p-2.5 text-center">
      <Icon className="mx-auto h-4 w-4 text-primary" />
      <div className="mt-1 font-display text-lg font-semibold leading-none">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

/* ─────────────────────────────  billing comparison  ───────────────────────────── */

function BillingComparison({ starter, growth }: { starter: ApiPlan; growth: ApiPlan }) {
  const starterMonthly = starter.price;
  const growthAnnual = growth.price;
  const growthMonthlyEq = Math.round(growthAnnual / 12);
  const starter12mo = starterMonthly * 12;
  const savings = starter12mo - growthAnnual;
  const discountPct = starter12mo > 0 ? (savings / starter12mo) * 100 : 0;

  const rows: [string, string, string][] = [
    ["Billing frequency", "Monthly", "Annual"],
    ["Amount charged", `${inr(starterMonthly)}/month`, `${inr(growthAnnual)}/year`],
    ["Effective monthly cost", inr(starterMonthly), inr(growthMonthlyEq)],
    ["Vehicles included", `${starter.maxVehicles === UNLIMITED_SENTINEL ? "∞" : starter.maxVehicles}`, `${growth.maxVehicles === UNLIMITED_SENTINEL ? "∞" : growth.maxVehicles}`],
    ["Extra vehicle", `₹${OVERAGE_PER_VEHICLE} each`, `₹${OVERAGE_PER_VEHICLE} each`],
    ["Annual savings", "—", inr(savings)],
    ["Discount", "—", `${discountPct.toFixed(2)}%`],
  ];

  return (
    <section className="border-t border-border bg-surface/30 py-20 sm:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <div className="text-center">
          <div className="text-xs font-medium uppercase tracking-[0.18em] text-primary">Monthly vs annual</div>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Billing comparison.</h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">Same complete platform — annual billing just costs less per month and includes more vehicles.</p>
        </div>

        <div className="mt-12 overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-background/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-5 py-4 text-left font-normal">Detail</th>
                <th className="px-5 py-4 text-center font-normal">Starter</th>
                <th className="px-5 py-4 text-center font-normal text-primary">Growth</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <tr key={r[0]} className={i % 2 ? "bg-background/30" : ""}>
                  <td className="px-5 py-3.5 font-medium">{r[0]}</td>
                  <td className="px-5 py-3.5 text-center text-muted-foreground">{r[1]}</td>
                  <td className="px-5 py-3.5 text-center font-medium text-primary">{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Downgrades keep your existing data; new additions pause if you exceed a plan&rsquo;s limit.
        </p>
      </div>
    </section>
  );
}
